import React, { useState, useEffect, useMemo } from 'react';
import TabNavigation from '../../components/ui/TabNavigation';
import PersonColorLegend from '../../components/ui/PersonColorLegend';
import QuickActionBar from '../../components/ui/QuickActionBar';
import TimeSlot from './components/TimeSlot';
import TaskSummary from './components/TaskSummary';
import AddTaskForm from './components/AddTaskForm';
import MemberFilter from './components/MemberFilter';
import DailyTaskExportControls from './components/DailyTaskExportControls';
import { generateRecurringInstances, hasRecurringSchedule } from '../../utils/recurringUtils';
import { getUpcomingReminders, shouldShowReminder } from '../../utils/notificationUtils';
import ReminderIndicator from '../family-calendar-view/components/ReminderIndicator';
import { checkAchievements } from '../../utils/achievementUtils';
import AchievementUnlockModal from '../../components/ui/AchievementUnlockModal';
import AchievementNotification from '../../components/ui/AchievementNotification';
import { Helmet } from 'react-helmet';
import { isSessionUnlocked } from '../../utils/parentalLockService';
import DateWeatherDisplay from './components/DateWeatherDisplay';
import { Settings } from 'lucide-react';
import { taskService } from '../../services/taskService';
import { familyService } from '../../services/familyService';
import { useAuth } from '../../contexts/AuthContext';



const DailyTaskManagement = () => {
  const { user } = useAuth();
  const [familyGroupId, setFamilyGroupId] = useState(null);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [persons, setPersons] = useState([
    {
      id: 1,
      name: 'Sarah',
      role: 'Mom',
      color: '#EF4444',
      initials: 'SM'
    },
    {
      id: 2,
      name: 'Michael',
      role: 'Dad',
      color: '#3B82F6',
      initials: 'MD'
    },
    {
      id: 3,
      name: 'Emma',
      role: 'Daughter',
      color: '#EC4899',
      initials: 'ED'
    },
    {
      id: 4,
      name: 'Lucas',
      role: 'Son',
      color: '#10B981',
      initials: 'LS'
    },
    {
      id: 5,
      name: 'Olivia',
      role: 'Daughter',
      color: '#F59E0B',
      initials: 'OD'
    },
    {
      id: 6,
      name: 'Noah',
      role: 'Son',
      color: '#8B5CF6',
      initials: 'NS'
    }
  ]);

  const [tasks, setTasks] = useState([]);

  const [selectedPersons, setSelectedPersons] = useState(persons?.map(p => p?.id));
  const [showAddForm, setShowAddForm] = useState(false);
  const [draggedTask, setDraggedTask] = useState(null);
  const [activeReminders, setActiveReminders] = useState([]);
  const [dismissedReminders, setDismissedReminders] = useState([]);
  const [dayStartHour, setDayStartHour] = useState(() => {
    const saved = localStorage?.getItem('dailyTaskStartHour');
    return saved ? parseInt(saved, 10) : 6;
  });
  const [dayEndHour, setDayEndHour] = useState(() => {
    const saved = localStorage?.getItem('dailyTaskEndHour');
    return saved ? parseInt(saved, 10) : 22;
  });
  const [showTimeSettings, setShowTimeSettings] = useState(false);

  // Mock notification settings
  const notificationSettings = [
    { memberId: 1, enabled: true, frequency: ['15min', '1day'], deliveryTiming: 'smart', quietHoursStart: '22:00', quietHoursEnd: '07:00', notifyForRecurring: true },
    { memberId: 2, enabled: true, frequency: ['30min', '1day'], deliveryTiming: 'smart', quietHoursStart: '22:00', quietHoursEnd: '07:00', notifyForRecurring: true },
    { memberId: 3, enabled: true, frequency: ['15min'], deliveryTiming: 'smart', quietHoursStart: '22:00', quietHoursEnd: '07:00', notifyForRecurring: true },
    { memberId: 4, enabled: true, frequency: ['15min', '1hour'], deliveryTiming: 'smart', quietHoursStart: '22:00', quietHoursEnd: '07:00', notifyForRecurring: true },
    { memberId: 5, enabled: true, frequency: ['30min'], deliveryTiming: 'smart', quietHoursStart: '22:00', quietHoursEnd: '07:00', notifyForRecurring: true },
    { memberId: 6, enabled: true, frequency: ['15min', '1day'], deliveryTiming: 'smart', quietHoursStart: '22:00', quietHoursEnd: '07:00', notifyForRecurring: true }
  ];

  const [selectedPerson, setSelectedPerson] = useState('all');
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const [currentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [upcomingReminders, setUpcomingReminders] = useState([]);
  const [memberAchievements, setMemberAchievements] = useState({});
  const [achievementToShow, setAchievementToShow] = useState(null);
  const [achievementNotifications, setAchievementNotifications] = useState([]);
  const { userProfile } = useAuth();
  const [realtimeChannel, setRealtimeChannel] = useState(null);

  // Load achievements from localStorage on mount
  useEffect(() => {
    const savedAchievements = localStorage?.getItem('memberAchievements');
    if (savedAchievements) {
      try {
        setMemberAchievements(JSON?.parse(savedAchievements));
      } catch (e) {
        console?.error('Failed to load achievements:', e);
      }
    }
  }, []);

  // Save achievements to localStorage whenever they change
  useEffect(() => {
    if (Object?.keys(memberAchievements)?.length > 0) {
      localStorage?.setItem('memberAchievements', JSON?.stringify(memberAchievements));
    }
  }, [memberAchievements]);

  // Save day start hour to localStorage
  useEffect(() => {
    localStorage?.setItem('dailyTaskStartHour', dayStartHour?.toString());
  }, [dayStartHour]);

  // Save day end hour to localStorage
  useEffect(() => {
    localStorage?.setItem('dailyTaskEndHour', dayEndHour?.toString());
  }, [dayEndHour]);

  // Load family data and tasks
  useEffect(() => {
    if (user) {
      loadFamilyData();
    }
  }, [user]);

  const loadFamilyData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get family group
      const familyGroup = await familyService?.getFamilyGroupByUserId(user?.id);
      if (!familyGroup) {
        setError('No family group found');
        setLoading(false);
        return;
      }

      setFamilyGroupId(familyGroup?.id);

      // Get family members
      const members = await familyService?.getFamilyMembers(familyGroup?.id);
      setFamilyMembers(members || []);

      // Get daily tasks
      const dailyTasks = await taskService?.getDailyTasks(familyGroup?.id);
      
      // Transform tasks to match component format
      const transformedTasks = dailyTasks?.map(task => ({
        id: task?.id,
        title: task?.title,
        description: task?.description,
        personId: task?.assigned_member?.id,
        personName: task?.assigned_member?.display_name,
        personColor: task?.assigned_member?.color,
        personInitials: task?.assigned_member?.initials,
        time: task?.time_slot ? parseInt(task?.time_slot) : null,
        priority: task?.priority,
        completed: task?.status === 'completed',
        isRecurring: task?.is_recurring,
        recurringConfig: task?.is_recurring ? {
          frequency: task?.recurrence_frequency,
          pattern: task?.recurrence_pattern,
          endDate: task?.recurrence_end_date
        } : null,
        createdAt: task?.created_at
      })) || [];

      setTasks(transformedTasks);
      setLoading(false);
    } catch (err) {
      console.error('Error loading family data:', err);
      setError(err?.message || 'Failed to load family data');
      setLoading(false);
    }
  };

  const timeSlots = useMemo(() => {
    const slots = [];
    let currentHour = dayStartHour;
    const endHour = dayEndHour;
    
    // Calculate number of hours to display
    let hoursToDisplay;
    if (endHour >= dayStartHour) {
      hoursToDisplay = endHour - dayStartHour + 1;
    } else {
      // Handle case where end time is next day (e.g., start 22:00, end 06:00)
      hoursToDisplay = (24 - dayStartHour) + endHour + 1;
    }
    
    for (let i = 0; i < hoursToDisplay; i++) {
      slots?.push((currentHour + i) % 24);
    }
    
    return slots;
  }, [dayStartHour, dayEndHour]);

  const getFilteredTasks = () => {
    return tasks?.filter(task => selectedPersons?.includes(task?.personId));
  };

  const getTasksByTime = (time) => {
    return getFilteredTasks()?.filter(task => task?.time === time);
  };

  const handleTaskComplete = (taskId, completed) => {
    setTasks(prev => {
      const updatedTasks = prev?.map(task => {
        if (task?.id === taskId) {
          return { 
            ...task, 
            completed,
            completedAt: completed ? new Date()?.toISOString() : null
          };
        }
        return task;
      });

      // Check for achievements if task was completed
      if (completed) {
        const task = prev?.find(t => t?.id === taskId);
        if (task?.personId) {
          const previousAchievements = memberAchievements?.[task?.personId] || [];
          const { newlyUnlocked, allUnlocked } = checkAchievements(
            updatedTasks,
            task?.personId,
            previousAchievements
          );

          if (newlyUnlocked?.length > 0) {
            // Update achievements state
            setMemberAchievements(prev => ({
              ...prev,
              [task?.personId]: allUnlocked
            }));

            // Show modal for first achievement
            const member = persons?.find(p => p?.id === task?.personId);
            setAchievementToShow({
              achievement: newlyUnlocked?.[0],
              memberName: member?.name,
              memberColor: member?.color
            });

            // Queue notifications for remaining achievements
            if (newlyUnlocked?.length > 1) {
              setAchievementNotifications(prev => [
                ...prev,
                ...newlyUnlocked?.slice(1)?.map(ach => ({
                  achievement: ach,
                  memberName: member?.name,
                  memberColor: member?.color,
                  id: `${ach?.id}-${Date?.now()}`
                }))
              ]);
            }
          }
        }
      }

      return updatedTasks;
    });
  };

  const handleTaskDelete = (taskId) => {
    if (!isSessionUnlocked()) {
      alert('Please unlock parental lock to delete tasks');
      return;
    }
    setTasks(prevTasks => prevTasks?.filter(task => task?.id !== taskId));
  };

  const handleAddTask = async (newTask) => {
    try {
      const taskData = {
        family_group_id: familyGroupId,
        title: newTask?.title,
        description: newTask?.description || null,
        assigned_member_id: newTask?.personId,
        time_slot: newTask?.time?.toString(),
        priority: newTask?.priority,
        status: 'pending'
      };

      let createdTask;
      if (newTask?.recurringConfig) {
        // Create recurring task
        createdTask = await taskService?.createRecurringDailyTask(taskData, newTask?.recurringConfig);
      } else {
        // Create regular task
        createdTask = await taskService?.createDailyTask(taskData);
      }

      // Transform and add to state
      const transformedTask = {
        id: createdTask?.id,
        title: createdTask?.title,
        description: createdTask?.description,
        personId: createdTask?.assigned_member?.id,
        personName: createdTask?.assigned_member?.display_name,
        personColor: createdTask?.assigned_member?.color,
        personInitials: createdTask?.assigned_member?.initials,
        time: createdTask?.time_slot ? parseInt(createdTask?.time_slot) : null,
        priority: createdTask?.priority,
        completed: false,
        isRecurring: createdTask?.is_recurring,
        recurringConfig: createdTask?.is_recurring ? {
          frequency: createdTask?.recurrence_frequency,
          pattern: createdTask?.recurrence_pattern,
          endDate: createdTask?.recurrence_end_date
        } : null,
        createdAt: createdTask?.created_at
      };

      setTasks(prev => [...prev, transformedTask]);
      setShowAddForm(false);
    } catch (err) {
      console.error('Error creating task:', err);
      setError(err?.message || 'Failed to create task');
    }
  };

  const handleUpdateTask = (updatedTask) => {
    setTasks(tasks?.map(task => task?.id === updatedTask?.id ? updatedTask : task));
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await taskService?.deleteDailyTask(taskId);
      setTasks(prev => prev?.filter(t => t?.id !== taskId));
    } catch (err) {
      console.error('Error deleting task:', err);
      setError(err?.message || 'Failed to delete task');
    }
  };

  const handleTaskDragStart = (task) => {
    setDraggedTask(task);
  };

  const handleTaskDrop = (newTime) => {
    if (draggedTask && draggedTask?.time !== newTime) {
      setTasks(prevTasks =>
        prevTasks?.map(task =>
          task?.id === draggedTask?.id ? { ...task, time: newTime } : task
        )
      );
    }
    setDraggedTask(null);
  };

  const handleQuickAction = (action) => {
    if (action?.id === 'add-task' || action?.id === 'add-time-block') {
      setShowAddForm(true);
    }
  };

  const handlePersonUpdate = (updatedPersons) => {
    setPersons(updatedPersons);
  };

  const formatDate = (date) => {
    return date?.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getTasksForTimeSlot = (hour) => {
    const today = new Date()?.toISOString()?.split('T')?.[0];
    
    // Direct tasks for this hour
    const directTasks = tasks?.filter(task => task?.time === hour);
    
    // Generate recurring task instances for today
    const recurringTasks = tasks
      ?.filter(task => hasRecurringSchedule(task))
      ?.flatMap(task => {
        const instances = generateRecurringInstances(
          task,
          task?.recurringConfig,
          today,
          today
        );
        return instances?.filter(instance => instance?.time === hour);
      });

    return [...directTasks, ...recurringTasks];
  };

  // Check for upcoming reminders
  useEffect(() => {
    const checkReminders = () => {
      const upcomingReminders = getUpcomingReminders(tasks, notificationSettings);
      const activeOnes = upcomingReminders?.filter(r => 
        shouldShowReminder(r) && !dismissedReminders?.includes(r?.id)
      );
      setActiveReminders(activeOnes);
    };

    checkReminders();
    const interval = setInterval(checkReminders, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [tasks, notificationSettings, dismissedReminders]);

  // Real-time subscription for daily tasks
  useEffect(() => {
    if (!familyGroupId) return;

    const channel = taskService?.subscribeToDailyTasks(familyGroupId, (payload) => {
      if (payload?.eventType === 'INSERT') {
        loadFamilyData();
      } else if (payload?.eventType === 'UPDATE') {
        loadFamilyData();
      } else if (payload?.eventType === 'DELETE') {
        setTasks(prev => prev?.filter(t => t?.id !== payload?.old?.id));
      }
    });

    return () => {
      if (channel) {
        taskService?.unsubscribe(channel);
      }
    };
  }, [familyGroupId]);

  const handleDismissReminder = (reminderId) => {
    setDismissedReminders(prev => [...prev, reminderId]);
  };

  const filteredTasks = useMemo(() => {
    return tasks?.filter(task => {
      const matchesPerson = selectedPerson === 'all' || task?.personId === selectedPerson;
      const matchesPriority = selectedPriority === 'all' || task?.priority === selectedPriority;
      const matchesStatus = selectedStatus === 'all' || 
        (selectedStatus === 'completed' && task?.completed) ||
        (selectedStatus === 'pending' && !task?.completed);
      const matchesSearch = !searchQuery || 
        task?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        task?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase());
      
      return matchesPerson && matchesPriority && matchesStatus && matchesSearch;
    });
  }, [tasks, selectedPerson, selectedPriority, selectedStatus, searchQuery]);

  const handleTimeSettingsSave = (newStartHour) => {
    setDayStartHour(newStartHour);
    setShowTimeSettings(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Daily Tasks - Starling Schedule</title>
      </Helmet>
      {/* Achievement Unlock Modal */}
      {achievementToShow && (
        <AchievementUnlockModal
          achievement={achievementToShow?.achievement}
          memberName={achievementToShow?.memberName}
          memberColor={achievementToShow?.memberColor}
          onClose={() => setAchievementToShow(null)}
        />
      )}
      {/* Achievement Notifications */}
      {achievementNotifications?.map((notification, index) => (
        <div
          key={notification?.id}
          style={{ top: `${4 + index * 6}rem` }}
        >
          <AchievementNotification
            achievement={notification?.achievement}
            memberName={notification?.memberName}
            memberColor={notification?.memberColor}
            onClose={() => {
              setAchievementNotifications(prev => 
                prev?.filter(n => n?.id !== notification?.id)
              );
            }}
          />
        </div>
      ))}
      <TabNavigation />
      <main className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
        {/* Date and Weather Display */}
        <DateWeatherDisplay />
        
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4 md:mb-6">
            <div>
              <h1 className="text-h1 font-heading text-foreground mb-2">
                Daily Task Management
              </h1>
              <p className="text-muted-foreground">
                Organize and track family tasks throughout the day
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowTimeSettings(!showTimeSettings)}
                className="flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-lg hover:bg-accent transition-colors"
                title="Customize day start and end time"
              >
                <Settings className="w-4 h-4" />
                <span className="text-sm">Day: {dayStartHour % 12 || 12}:{(0)?.toString()?.padStart(2, '0')} {dayStartHour < 12 ? 'AM' : 'PM'} - {dayEndHour % 12 || 12}:{(0)?.toString()?.padStart(2, '0')} {dayEndHour < 12 ? 'AM' : 'PM'}</span>
              </button>
              <DailyTaskExportControls 
                tasks={getFilteredTasks()} 
                persons={persons}
                currentDate={currentDate}
              />
            </div>
          </div>

          {showTimeSettings && (
            <div className="mb-4 p-4 bg-card border border-border rounded-lg">
              <h3 className="text-lg font-semibold mb-3">Day Time Settings</h3>
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <label className="text-sm text-muted-foreground min-w-[140px]">
                    Day starts at:
                  </label>
                  <select
                    value={dayStartHour}
                    onChange={(e) => setDayStartHour(parseInt(e?.target?.value, 10))}
                    className="px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    {Array.from({ length: 24 }, (_, i) => i)?.map(hour => (
                      <option key={hour} value={hour}>
                        {(hour % 12 || 12)?.toString()?.padStart(2, '0')}:00 {hour < 12 ? 'AM' : 'PM'}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <label className="text-sm text-muted-foreground min-w-[140px]">
                    Day ends at:
                  </label>
                  <select
                    value={dayEndHour}
                    onChange={(e) => setDayEndHour(parseInt(e?.target?.value, 10))}
                    className="px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    {Array.from({ length: 24 }, (_, i) => i)?.map(hour => (
                      <option key={hour} value={hour}>
                        {(hour % 12 || 12)?.toString()?.padStart(2, '0')}:00 {hour < 12 ? 'AM' : 'PM'}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={() => setShowTimeSettings(false)}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Done
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                The daily task view will display hours from your start time to your end time.
              </p>
            </div>
          )}

          <MemberFilter
            persons={persons}
            selectedPersons={selectedPersons}
            onFilterChange={setSelectedPersons}
          />
        </div>

        {showAddForm && (
          <div className="mb-6 md:mb-8">
            <AddTaskForm
              persons={persons}
              onAddTask={handleAddTask}
              onCancel={() => setShowAddForm(false)}
            />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          <div className="lg:col-span-2 space-y-4 md:space-y-6">
            {timeSlots?.map(time => (
              <TimeSlot
                key={time}
                time={time}
                tasks={getTasksByTime(time)}
                onTaskComplete={handleTaskComplete}
                onTaskDelete={handleTaskDelete}
                onTaskDragStart={handleTaskDragStart}
                onTaskDrop={handleTaskDrop}
                onTaskDragOver={() => {}}
                persons={persons}
              />
            ))}
          </div>

          <div className="space-y-6 md:space-y-8">
            <TaskSummary tasks={getFilteredTasks()} persons={persons} />
            
            <PersonColorLegend
              layout="vertical"
              onPersonUpdate={handlePersonUpdate}
            />
          </div>
        </div>

        <div className="mt-6">
          <ReminderIndicator 
            reminders={activeReminders}
            onDismiss={handleDismissReminder}
          />
        </div>
      </main>
      <QuickActionBar
        context="daily"
        onActionSelect={handleQuickAction}
      />
    </div>
  );
};

export default DailyTaskManagement;